import { StrictMode, Suspense } from "react";
import { createRoot } from "react-dom/client";
import { RouterProvider } from "react-router-dom";
import { AppProviders } from "./app/providers/AppProviders";
import { router } from "./app/router";
import { RootErrorBoundary } from "./app/routes/RootErrorBoundary";
import { initKeycloakConfig } from "./features/auth/config/keycloak";
import { registerServiceWorker } from "./features/notifications/webPush";
import { applyTheme } from "./shared/hooks/useApplyTheme";
import { initCrashReporter } from "./shared/monitoring/sentry";
import { usePreferencesStore } from "./shared/stores/preferencesStore";
import { Spinner } from "./shared/ui";
import "./index.css";

// Remplace index.ts + App.tsx de l'app Expo (registerRootComponent).
// Disparaissent au passage : react-native-gesture-handler, react-native-get-random-values
// (crypto.getRandomValues est natif au navigateur), expo-splash-screen et
// expo-status-bar.

// Hydrate la config Keycloak depuis le Gateway en parallèle du boot.
// Non bloquant : si le Gateway est down, on reste sur la config des variables
// VITE_KEYCLOAK_*. Port de l'appel qui vivait dans index.ts (natif).
void initKeycloakConfig();

// Thème posé AVANT le premier rendu de React.
//
// Le store `persist` de zustand hydrate localStorage de façon SYNCHRONE au
// premier accès : la préférence est donc déjà connue ici. Attendre un effet de
// React peindrait l'app en clair pendant une frame, puis la basculerait en
// sombre — le flash blanc classique, particulièrement violent de nuit.
applyTheme(usePreferencesStore.getState().theme);

// Monitoring — no-op si VITE_SENTRY_DSN est absent.
initCrashReporter();

// Enregistre le worker indépendamment de la permission, gérée après connexion
// par useDeviceRegistration (avec repli sur un geste utilisateur si nécessaire).
void registerServiceWorker();

function ScreenFallback() {
  return (
    <div className="flex h-dvh items-center justify-center bg-white">
      <Spinner size={28} label="Chargement de l'écran…" />
    </div>
  );
}

const rootEl = document.getElementById("root");
if (!rootEl) throw new Error("[boot] #root introuvable dans index.html");

createRoot(rootEl).render(
  <StrictMode>
    {/* Barrière PLACÉE AU-DESSUS des providers, à dessein : les `errorElement`
        du routeur ne couvrent que ce qui se rend dans les routes. Une exception
        levée par un provider ou par le RouterProvider lui-même laissait un
        écran blanc, sans le moindre message ni moyen de repartir. */}
    <RootErrorBoundary>
      <AppProviders>
        {/* Frontière de Suspense pour les écrans chargés à la demande.
            Sans elle, React lève une erreur au premier écran paresseux.
            Le repli est volontairement minimal : il ne s'affiche que le temps de
            télécharger un fragment de code, en général quelques dizaines de ms. */}
        <Suspense fallback={<ScreenFallback />}>
          <RouterProvider router={router} />
        </Suspense>
      </AppProviders>
    </RootErrorBoundary>
  </StrictMode>,
);
